import React, { useEffect, useMemo, useState } from 'react'
import { Loader2, Plus } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { toast } from '@/components/ui/use-toast'
import { useMailboxes } from '../hooks/useMailboxes'
import MailboxCard from './MailboxCard'
import AddMailboxDialog from './AddMailboxDialog'

type Props = {
  workspaceId: string
}

function getStoredUserId(): string {
  try {
    return (JSON.parse(localStorage.getItem('user') || '{}') as { id?: string }).id ?? ''
  } catch {
    return ''
  }
}

const MailboxesPanel: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const { mailboxes, cap, loading, error, refetch } = useMailboxes(workspaceId)
  const [addOpen, setAddOpen] = useState(false)

  const userId = useMemo(getStoredUserId, [])

  const main = mailboxes.find((m) => m.role === 'main') ?? null
  const secondaries = mailboxes.filter((m) => m.role === 'secondary')
  const atCap = cap.secondariesInUse >= cap.maxSecondaries
  const canAdd = !!main && !atCap

  // Read `?mailbox=connected` after returning from the OAuth round-trip, flash
  // a toast, refresh, and clean the URL. Only this panel's own query flag is
  // consumed — anything else in the search string (e.g. ?tab=mailboxes that
  // keeps the Sales page on this tab) is preserved.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get('mailbox')
    if (outcome !== 'connected') return
    toast({ variant: 'success', title: 'Mailbox connected', description: 'Zeph can now send from this mailbox too.' })
    void refetch()
    params.delete('mailbox')
    const newSearch = params.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`)
    // refetch is a useCallback, stable per workspaceId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])

  const addButtonHint = (() => {
    if (!main) return 'Connect a main mailbox in Settings first.'
    if (atCap) return `Limit of ${cap.maxSecondaries} additional reached.`
    const remaining = cap.maxSecondaries - cap.secondariesInUse
    return `${remaining} slot${remaining === 1 ? '' : 's'} left`
  })()

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <span className="text-xs" style={{ color: t.mutedText }}>{addButtonHint}</span>
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          disabled={!canAdd}
          className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: t.accentGradient ?? t.accent }}
        >
          <Plus size={15} />
          Add mailbox
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-4" style={{ color: t.mutedText }}>
          <Loader2 size={14} className="animate-spin" />
          <span className="text-xs">Loading mailboxes…</span>
        </div>
      )}

      {!loading && error && (
        <div
          className="rounded-lg p-3 text-xs"
          style={{ backgroundColor: t.errorBackground, color: t.errorText }}
        >
          {error}
        </div>
      )}

      {!loading && !error && !main && secondaries.length === 0 && (
        <div
          className="rounded-xl p-6 text-center"
          style={{ backgroundColor: t.sectionBackground, color: t.mutedText, border: `1px dashed ${t.border}` }}
        >
          <p className="text-sm font-semibold mb-1" style={{ color: t.text }}>No mailboxes yet</p>
          <p className="text-xs">
            Connect your main mailbox in Settings → Integrations to let Zeph start sending.
          </p>
        </div>
      )}

      {!loading && !error && (main || secondaries.length > 0) && (
        <div className="flex flex-col gap-2.5">
          {main && <MailboxCard workspaceId={workspaceId} mailbox={main} onChanged={refetch} />}
          {secondaries.map((m) => (
            <MailboxCard key={m.id} workspaceId={workspaceId} mailbox={m} onChanged={refetch} />
          ))}
        </div>
      )}

      {addOpen && (
        <AddMailboxDialog
          workspaceId={workspaceId}
          userId={userId}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  )
}

export default React.memo(MailboxesPanel)
